/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  PAYMENT_MODES,
  type BillingSettingsInput,
  type PaymentMode,
} from '@therapy/shared-validators';
import { type ChangeEvent, useCallback, type JSX } from 'react';
import {
  type Control,
  type FieldErrors,
  type FieldPath,
  useController,
  type UseFormRegister,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type Ctrl = Control<BillingSettingsInput>;
export type Reg = UseFormRegister<BillingSettingsInput>;
export type Errs = FieldErrors<BillingSettingsInput>;

const LABEL = 'mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300';
const INPUT =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const ERR = 'mt-1 text-xs text-red-600';

/** A titled group of settings. */
export function SectionCard({
  title,
  children,
}: {
  readonly title: string;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="rounded-2xl border border-slate-200 p-5 dark:border-slate-700">
      <h3 className="mb-4 text-sm font-semibold text-slate-800 dark:text-white">{title}</h3>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

interface NumberFieldProps {
  readonly register: Reg;
  readonly name: FieldPath<BillingSettingsInput>;
  readonly label: string;
  readonly min: number;
  readonly max: number;
  readonly suffix?: string;
  readonly error?: string | undefined;
}

export function NumberField({
  register,
  name,
  label,
  min,
  max,
  suffix,
  error,
}: NumberFieldProps): JSX.Element {
  return (
    <div>
      <label htmlFor={name} className={LABEL}>
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          id={name}
          type="number"
          min={min}
          max={max}
          className={INPUT}
          aria-invalid={error !== undefined}
          {...register(name)}
        />
        {suffix !== undefined && <span className="text-sm text-slate-400">{suffix}</span>}
      </div>
      {error !== undefined && <p className={ERR}>{error}</p>}
    </div>
  );
}

export function TextField({
  register,
  name,
  label,
  error,
}: {
  readonly register: Reg;
  readonly name: FieldPath<BillingSettingsInput>;
  readonly label: string;
  readonly error?: string | undefined;
}): JSX.Element {
  return (
    <div>
      <label htmlFor={name} className={LABEL}>
        {label}
      </label>
      <input
        id={name}
        type="text"
        className={INPUT}
        aria-invalid={error !== undefined}
        {...register(name)}
      />
      {error !== undefined && <p className={ERR}>{error}</p>}
    </div>
  );
}

export function ToggleRow({
  register,
  name,
  label,
}: {
  readonly register: Reg;
  readonly name: FieldPath<BillingSettingsInput>;
  readonly label: string;
}): JSX.Element {
  const id = `bs-${name}`;
  return (
    <label
      htmlFor={id}
      className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 px-4 py-3 dark:border-slate-700"
    >
      <span className="text-sm font-medium text-slate-800 dark:text-slate-100">{label}</span>
      <input
        id={id}
        type="checkbox"
        className="focus-ring text-brand-600 h-4 w-4 rounded border-slate-300"
        {...register(name)}
      />
    </label>
  );
}

/**
 * A number input bound to a value stored ×100 (paise from rupees, or GST basis
 * points from a percentage) — the user types the human unit, we store the scaled
 * integer.
 */
export function ScaledField({
  control,
  name,
  label,
  suffix,
  error,
}: {
  readonly control: Ctrl;
  readonly name: 'gstRateBps' | 'noShowFeePaise';
  readonly label: string;
  readonly suffix?: string;
  readonly error?: string | undefined;
}): JSX.Element {
  const { field } = useController({ control, name });
  const value = field.value;
  const display = typeof value === 'number' ? String(value / 100) : '';
  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      field.onChange(raw === '' ? 0 : Math.round(Number(raw) * 100));
    },
    [field],
  );
  return (
    <div>
      <label htmlFor={name} className={LABEL}>
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          id={name}
          type="number"
          min="0"
          step="0.01"
          className={INPUT}
          value={display}
          onChange={onChange}
          onBlur={field.onBlur}
          aria-invalid={error !== undefined}
        />
        {suffix !== undefined && <span className="text-sm text-slate-400">{suffix}</span>}
      </div>
      {error !== undefined && <p className={ERR}>{error}</p>}
    </div>
  );
}

function ModeCheckbox({
  mode,
  checked,
  onToggle,
}: {
  readonly mode: PaymentMode;
  readonly checked: boolean;
  readonly onToggle: (mode: PaymentMode) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const change = useCallback(() => {
    onToggle(mode);
  }, [onToggle, mode]);
  return (
    <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm dark:border-slate-700">
      <input
        type="checkbox"
        className="focus-ring text-brand-600 h-4 w-4 rounded border-slate-300"
        checked={checked}
        onChange={change}
      />
      {t(`settings.billing.mode.${mode}`)}
    </label>
  );
}

/** Multi-select of accepted payment modes (CR-010). */
export function PaymentModesField({
  control,
  error,
}: {
  readonly control: Ctrl;
  readonly error?: string | undefined;
}): JSX.Element {
  const { t } = useTranslation();
  const { field } = useController({ control, name: 'acceptedPaymentModes' });
  const selected = field.value;
  const onToggle = useCallback(
    (mode: PaymentMode) => {
      const cur = field.value;
      field.onChange(cur.includes(mode) ? cur.filter((m) => m !== mode) : [...cur, mode]);
    },
    [field],
  );
  return (
    <div>
      <span className={LABEL}>{t('settings.billing.acceptedModes')}</span>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {PAYMENT_MODES.map((mode) => (
          <ModeCheckbox
            key={mode}
            mode={mode}
            checked={selected.includes(mode)}
            onToggle={onToggle}
          />
        ))}
      </div>
      {error !== undefined && <p className={ERR}>{error}</p>}
    </div>
  );
}
